"use client";
import React, { useEffect } from "react";
import Slider from "react-slick";
import { motion } from "framer-motion";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaUserTie, FaUserAlt } from "react-icons/fa";
import StatisticsSection from "../HomePage/StatisticsSection";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.03) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const testimonials = [
  {
    id: 1,
    name: "Ahmad Raza",
    handle: "@ahmadraza_dev",
    gender: "male",
    title: "Outstanding Web Development",
    feedback: "fiieVe delivered our e-commerce platform on time and beyond our expectations. Their attention to detail, clean code, and proactive communication made the entire process smooth. Our sales have grown 40% since launch.",
  },
  {
    id: 2,
    name: "Sana Mirza",
    handle: "@sanamirza_ux",
    gender: "female",
    title: "Brilliant UI/UX Design Work",
    feedback: "The design team at fiieVe completely transformed our app's interface. They took time to understand our users and delivered a clean, intuitive experience. Our user retention improved significantly after the redesign.",
  },
  {
    id: 3,
    name: "Bilal Tariq",
    handle: "@bilaltariq_ceo",
    gender: "male",
    title: "Powerful AI Automation Solution",
    feedback: "We hired fiieVe to automate our internal workflows using AI and the results were remarkable. Manual processes that took hours are now done in minutes. Their team was knowledgeable, responsive, and truly professional.",
  },
  {
    id: 4,
    name: "Nadia Hassan",
    handle: "@nadiahassan_biz",
    gender: "female",
    title: "Reliable Mobile App Development",
    feedback: "fiieVe built our iOS and Android app from scratch. The performance is excellent and the user experience is exactly what we envisioned. They provided regular updates throughout and were always available for feedback.",
  },
];

export default function Testimonial() {
  const { isDark } = useTheme();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const settings = {
    dots: true,
    infinite: false,
    speed: 800,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: false,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 768,  settings: { slidesToShow: 1, slidesToScroll: 1 } },
      { breakpoint: 640,  settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  const greenHeading  = isDark ? "text-green-400" : "text-green-700";
  const headingText   = isDark ? "text-white"     : "text-gray-900";
  const cardBg        = isDark ? "bg-[#0a0a0a]"  : "bg-[#1a2e1a]";
  const feedbackText  = isDark ? "text-gray-400"  : "text-gray-300";
  const handleText    = isDark ? "text-gray-500"  : "text-gray-400";
  const avatarBg      = isDark ? "bg-[#0d1a0f]"  : "bg-[#0d1a0f]";

  return (
    <div
      className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900"}`}
      style={isDark ? darkGridStyle : lightGridStyle}
    >
      {/* Light mode atmospheric glows */}
      {!isDark && (
        <div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{
            position: "absolute", top: "-10%", left: "-5%",
            width: "55vw", height: "55vw",
            background: "radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)",
            filter: "blur(40px)",
          }} />
          <div style={{
            position: "absolute", top: "-8%", right: "-8%",
            width: "50vw", height: "50vw",
            background: "radial-gradient(circle, rgba(251,146,60,0.11) 0%, transparent 70%)",
            filter: "blur(40px)",
          }} />
          <div style={{
            position: "absolute", top: "50%", left: "20%",
            width: "60vw", height: "40vw",
            background: "radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)",
            filter: "blur(55px)",
          }} />
        </div>
      )}

      {/* Dark mode subtle radial glow */}
      {isDark && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div style={{
            position: "absolute", top: "10%", left: "10%",
            width: "50vw", height: "50vw",
            background: "radial-gradient(circle, rgba(74,222,128,0.05) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
          <div style={{
            position: "absolute", bottom: "0%", right: "5%",
            width: "40vw", height: "40vw",
            background: "radial-gradient(circle, rgba(251,146,60,0.04) 0%, transparent 70%)",
            filter: "blur(45px)",
          }} />
        </div>
      )}

      <div className="relative" style={{ zIndex: 1 }}>

        {/* ===== Hero Section ===== */}
       <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/Images/bg.png')" }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
          <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
            <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
             Testimonials
            </h1>
            <p className="mt-3 text-sm md:text-base text-white">
              Home / <span className="text-green-400">Testimonials</span>
            </p>
          </div>
        </div>

        {/* ===== Testimonials ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto px-4 pt-8 pb-4 -mt-2"
        >
          {!isDark && (
            <style>{`
              .slick-prev:before, .slick-next:before { color: #166534 !important; }
            `}</style>
          )}
          <div className="text-center mb-8 md:mb-12">
            <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>
              What People Say
            </h2>
            <h2 className={`text-3xl md:text-4xl font-bold ${greenHeading}`}>
              Our Clients Feedback
            </h2>
          </div>

          <Slider {...settings}>
            {testimonials.map((t) => (
              <div key={t.id} className="px-3">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  className={`${cardBg} border border-green-400/30 rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-green-400/10 hover:border-green-400/60 transition-all duration-300 flex flex-col sm:flex-row items-center sm:items-start gap-5 h-full`}
                >
                  <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-green-400/40 ${avatarBg} flex items-center justify-center flex-shrink-0`}>
                    {t.gender === "male"
                      ? <FaUserTie size={28} className="text-green-400" />
                      : <FaUserAlt size={24} className="text-green-300" />
                    }
                  </div>

                  <div className="text-center sm:text-left flex flex-col gap-2">
                    <h3 className="text-lg font-bold text-white">{t.title}</h3>
                    <p className={`${feedbackText} text-sm leading-relaxed`}>{t.feedback}</p>
                    <div className="flex items-center justify-center sm:justify-start gap-0.5 mt-1">
                      {Array(5).fill().map((_, i) => (
                        <span key={i} className="text-green-400 text-lg">★</span>
                      ))}
                    </div>
                    <p className="text-green-400 font-semibold text-sm">{t.name}</p>
                    <p className={`${handleText} text-xs`}>{t.handle}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </Slider>
        </motion.div>

        {/* ===== Video Section ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto px-4 mt-14 mb-12"
        >
          <video
            src="../Videos/softwarehouse.mp4"
            autoPlay
            loop
            muted
            controls
            className="w-full h-[300px] md:h-[400px] rounded-xl shadow-lg object-cover"
          />
        </motion.div>

        {/* ===== Statistics Section ===== */}
        <StatisticsSection />
      </div>
    </div>
  );
}